import { isVietnamCompany } from './vietnam';

// CPI-977: the Vietnam pricing-package decisions in one place, as plain functions - each one is a
// rule book-api enforces as well. Every export returns the neutral answer off Vietnam.

// "Booked or beyond", where the pricing package freezes. Mirrors book-api's IsBookedOrBeyond.
export const BOOKED_OR_BEYOND_STATUS_IDS = ['booked', 'sold_spa_signed'];

export const isBookedOrBeyond = statusID => BOOKED_OR_BEYOND_STATUS_IDS.includes(statusID);

// CPI-977 requirements 7 and 11: one lock rule for the pricing package and the additional fee
// package, mirroring book-api's vnPackageLockKey. Reserved locks everyone but HQ
// (auth.isHeadquarters, the DEVELOPER_HEADQUARTERS group); Booked and Sold lock everyone, and the
// unit has to be cancelled to change the scheme. Malaysia keeps its own rule
// (BookingPricingPackage.jsx disablePricingEditAfterSold).
export const VN_PACKAGE_LOCK_MESSAGES = {
  pricing: {
    booked: 'Pricing package is locked once the unit is booked',
    reserved:
      'Only a Headquarters user can change the pricing package while the unit is reserved',
  },
  fee: {
    booked: 'Additional fee package is locked once the unit is booked',
    reserved:
      'Only a Headquarters user can change the additional fee package while the unit is reserved',
  },
};

// The screens.json key explaining why the package is locked, or null when it is not locked.
export const vnPackageLockMessage = (auth, statusID, kind = 'pricing') => {
  if (!isVietnamCompany(auth)) {
    return null;
  }
  const messages = VN_PACKAGE_LOCK_MESSAGES[kind] || VN_PACKAGE_LOCK_MESSAGES.pricing;
  if (isBookedOrBeyond(statusID)) {
    return messages.booked;
  }
  if (statusID === 'reserved' && !(auth && auth.isHeadquarters)) {
    return messages.reserved;
  }
  return null;
};

export const isPricingPackageLocked = (auth, statusID, kind = 'pricing') =>
  vnPackageLockMessage(auth, statusID, kind) !== null;

// CPI-977 requirement 1: Save stays disabled until a package is chosen. In Vietnam that is always
// required (Bookings/Form.jsx passes isVietnamCompany || the project's own
// require_pricing_select_during_reserve). The third argument is the "Base Selling Price" sentinel,
// which counts as nothing selected because the form clears it before POSTing.
export const isPricingPackageMissing = (isRequired, unitPackagePricingID, baseSellingPriceID) => {
  if (!isRequired) {
    return false;
  }
  if (!unitPackagePricingID) {
    return true;
  }
  return !!baseSellingPriceID && String(unitPackagePricingID) === String(baseSellingPriceID);
};

// CPI-977 S3: the waiver is a discount line matched by code, so renaming it cannot switch it off.
const BANK_GUARANTEE_WAIVER_CODE = 'BGW';

export const hasBankGuaranteeWaiverLine = (packagePricing) => {
  const calculations = (packagePricing && packagePricing.calculations) || [];
  return calculations.some(c => c && c.code === BANK_GUARANTEE_WAIVER_CODE);
};

// CPI-977 AC8: the approved CRM booking fee book-api attaches to the booking, or null when there
// is none. Minor units whose exponent depends on the currency - never divide by 100 here.
export const approvedBookingFeePaid = (booking) => {
  if (!booking) {
    return null;
  }
  const amountMinor = Number(booking.srBookingFeePaidMinor);
  if (!amountMinor || !(amountMinor > 0)) {
    return null;
  }
  return {
    amountMinor,
    currencyCode: booking.srBookingFeeCurrency || 'VND',
  };
};

// The live total the preview response carries, or null when it is not a priced package.
export const livePackageTotal = (preview) => {
  const packagePricing = (preview && preview.packagePricing) || null;
  if (!packagePricing) {
    return null;
  }
  const total = Number(packagePricing.totalPrice);
  if (!Number.isFinite(total)) {
    return null;
  }
  return total;
};

// The live total recorded for one package id, or null - the map is keyed by the dropdown's value.
export const liveTotalFor = (liveTotals, packagePricingID) => {
  if (!liveTotals || !packagePricingID) {
    return null;
  }
  const total = Number(liveTotals[packagePricingID]);
  if (!Number.isFinite(total)) {
    return null;
  }
  return total;
};

// D-C5: the label the panel shows instead of passing a stored figure off as a live one.
export const VN_STORED_TOTAL_NOTICE = 'Stored package total — live price unavailable for your role';

// What the Vietnam price panel shows: the amount, whether it came from the server's own preview,
// and whether to print the D-C5 notice - the live total, else the stored total, with the notice
// only when a preview was actually refused.
export const vnPricePanelFigure = ({ preview, previewFailed, storedTotal } = {}) => {
  const live = livePackageTotal(preview);
  if (live !== null) {
    return { amount: live, isLive: true, showStoredNotice: false };
  }
  const stored = Number(storedTotal);
  return {
    amount: Number.isFinite(stored) ? stored : 0,
    isLive: false,
    showStoredNotice: !!previewFailed,
  };
};

// CPI-977 (PO, 5 Sep): the deposit is a percentage of the nett price, not the project's flat sum.
// The rate lives on the pricing package (package_pricings.deposit_rate, sent as depositRate) and
// book-api does the arithmetic; the two functions below are a unit conversion and a fallback.

// The column is a fraction (0.1000 = 10%) and the form talks percent; these conversions are the
// only place the two units meet. numeric(6,4) holds four decimals, so the box is worth two.
export const DEPOSIT_RATE_PERCENT_MAX = 100;
const DEPOSIT_RATE_PERCENT_DECIMALS = 2;

// '' for a package with no rate - the empty box IS the "no rate" state and must round-trip.
export const depositRateToPercent = (rate) => {
  if (rate === null || rate === undefined || rate === '') {
    return '';
  }
  const fraction = Number(rate);
  if (!Number.isFinite(fraction)) {
    return '';
  }
  // parseFloat drops the trailing zeros toFixed leaves behind, so 0.1 reads "10" and not "10.00".
  return String(parseFloat((fraction * 100).toFixed(DEPOSIT_RATE_PERCENT_DECIMALS)));
};

// null (not 0) for an empty or unusable box: book-api reads NULL as "no rate on this package"
// and 0 as "collect nothing".
export const depositPercentToRate = (percent) => {
  if (percent === null || percent === undefined || String(percent).trim() === '') {
    return null;
  }
  const value = Number(percent);
  if (!Number.isFinite(value) || value < 0 || value > DEPOSIT_RATE_PERCENT_MAX) {
    return null;
  }
  return Number((value / 100).toFixed(DEPOSIT_RATE_PERCENT_DECIMALS + 2));
};

// What the form's own validation asks. An empty box is valid.
export const isDepositRatePercentValid = (percent) => {
  if (percent === null || percent === undefined || String(percent).trim() === '') {
    return true;
  }
  const value = Number(percent);
  return Number.isFinite(value) && value >= 0 && value <= DEPOSIT_RATE_PERCENT_MAX;
};

// The server's computed deposit for a previewed scheme, or null. Two shapes are accepted, the
// package's own depositAmount first. Zero is a real answer and is kept.
export const previewDepositAmount = (preview) => {
  if (!preview) {
    return null;
  }
  const packagePricing = preview.packagePricing || null;
  const candidates = [
    packagePricing ? packagePricing.depositAmount : undefined,
    preview.depositAmount,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (candidate !== null && candidate !== undefined && candidate !== '') {
      const amount = Number(candidate);
      if (Number.isFinite(amount)) {
        return amount;
      }
    }
  }
  return null;
};

// The one deposit rule every CPI-977 screen asks: the computed amount when the server sent one,
// otherwise the flat figure that screen has always shown. isComputed says which it is.
export const depositToCollect = ({ preview, depositAmount, flatAmount } = {}) => {
  const computed = previewDepositAmount(preview);
  if (computed !== null) {
    return { amount: computed, isComputed: true };
  }
  // A saved booking has no preview: book-api stored the computed figure on the reservation. Zero
  // is NOT kept here - on a reservation it means the deposit has not been set yet.
  const stored = Number(depositAmount);
  if (Number.isFinite(stored) && stored > 0) {
    return { amount: stored, isComputed: true };
  }
  const flat = Number(flatAmount);
  return { amount: Number.isFinite(flat) ? flat : 0, isComputed: false };
};



// WEBPACK FOOTER //
// ./src/utils/vietnamPricing.js